/**
 * <yg-delivery> — finestra di consegna stimata, calcolata nel browser.
 *
 * Il blocco 'shipping' nativo del tema calcola la data in Liquid con 'now':
 * passa dalla cache di pagina di Shopify (quindi puo' mostrare una data vecchia)
 * e somma giorni di calendario, weekend e festivi compresi.
 * Qui il calcolo avviene al momento in cui l'utente guarda la pagina e conta
 * solo giorni lavorativi.
 *
 * L'ora di riferimento e' sempre quella italiana, non quella del visitatore:
 * un utente collegato da un altro fuso deve vedere lo stesso cut-off.
 *
 * Attributi:
 *   data-cutoff           ora limite per l'ordine odierno (0-23)
 *   data-processing-min   giorni lavorativi minimi ordine -> spedizione
 *   data-processing-max   giorni lavorativi massimi
 *   data-transit-min      giorni lavorativi minimi in transito
 *   data-transit-max      giorni lavorativi massimi
 *   data-label            testo prima delle date
 *   data-cutoff-text      testo del cut-off, {ora} viene sostituito
 */
if (!customElements.get('yg-delivery')) {
  const FUSO = 'Europe/Rome';

  /** Data civile a Roma: {a, m, g, ora}, indipendente dal fuso del visitatore. */
  function adessoARoma() {
    const parti = new Intl.DateTimeFormat('en-CA', {
      timeZone: FUSO,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const v = {};
    parti.forEach((p) => (v[p.type] = p.value));
    return {
      a: parseInt(v.year, 10),
      m: parseInt(v.month, 10),
      g: parseInt(v.day, 10),
      ora: parseInt(v.hour, 10) % 24,
    };
  }

  /**
   * Si lavora su date UTC per fare aritmetica sui giorni senza che l'ora legale
   * sposti nulla: qui conta solo il giorno del calendario, mai l'orario.
   */
  function aData(c) {
    return new Date(Date.UTC(c.a, c.m - 1, c.g));
  }

  function piuGiorni(d, n) {
    const x = new Date(d.getTime());
    x.setUTCDate(x.getUTCDate() + n);
    return x;
  }

  /** Pasqua gregoriana, algoritmo di Meeus/Jones/Butcher. */
  function pasqua(anno) {
    const a = anno % 19;
    const b = Math.floor(anno / 100);
    const c = anno % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const mese = Math.floor((h + l - 7 * m + 114) / 31);
    const giorno = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(anno, mese - 1, giorno));
  }

  const FESTE_FISSE = [
    [1, 1], // Capodanno
    [1, 6], // Epifania
    [4, 25], // Liberazione
    [5, 1], // Festa dei lavoratori
    [6, 2], // Festa della Repubblica
    [8, 15], // Ferragosto
    [11, 1], // Ognissanti
    [12, 8], // Immacolata
    [12, 25], // Natale
    [12, 26], // Santo Stefano
  ];

  const cacheFeste = {};
  function festeDi(anno) {
    if (cacheFeste[anno]) return cacheFeste[anno];
    const set = new Set(FESTE_FISSE.map(([m, g]) => `${m}-${g}`));
    // Lunedi' dell'Angelo: mobile, va calcolato
    const lunediAngelo = piuGiorni(pasqua(anno), 1);
    set.add(`${lunediAngelo.getUTCMonth() + 1}-${lunediAngelo.getUTCDate()}`);
    cacheFeste[anno] = set;
    return set;
  }

  function eLavorativo(d) {
    const giorno = d.getUTCDay();
    if (giorno === 0 || giorno === 6) return false;
    return !festeDi(d.getUTCFullYear()).has(`${d.getUTCMonth() + 1}-${d.getUTCDate()}`);
  }

  /** Avanza di n giorni lavorativi. Con n = 0 ritorna il primo giorno utile. */
  function piuGiorniLavorativi(d, n) {
    let x = new Date(d.getTime());
    while (!eLavorativo(x)) x = piuGiorni(x, 1);
    let restanti = n;
    while (restanti > 0) {
      x = piuGiorni(x, 1);
      if (eLavorativo(x)) restanti -= 1;
    }
    return x;
  }

  const fmtGiorno = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
  });
  const fmtGiornoMese = new Intl.DateTimeFormat('it-IT', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

  const ripulisci = (s) => s.replace(/\.$/, '').trim();

  customElements.define(
    'yg-delivery',
    class YgDelivery extends HTMLElement {
      connectedCallback() {
        try {
          this.render();
        } catch (e) {
          // Qualunque cosa vada storta, resta il testo generico gia' nell'HTML.
          console.warn('[yg-delivery]', e);
        }
      }

      num(nome, fallback) {
        const v = parseInt(this.dataset[nome], 10);
        return Number.isFinite(v) ? v : fallback;
      }

      render() {
        const uscita = this.querySelector('[data-yg-delivery-output]');
        if (!uscita) return;

        const cutoff = this.num('cutoff', 14);
        const lavMin = this.num('processingMin', 1);
        const lavMax = this.num('processingMax', 2);
        const transMin = this.num('transitMin', 1);
        const transMax = this.num('transitMax', 2);

        const ora = adessoARoma();
        let partenza = aData(ora);

        // Passato il cut-off, o fuori dai giorni lavorativi, l'ordine entra in
        // lavorazione dal primo giorno utile successivo.
        if (ora.ora >= cutoff || !eLavorativo(partenza)) {
          partenza = piuGiorniLavorativi(piuGiorni(partenza, 1), 0);
        }

        const da = piuGiorniLavorativi(partenza, lavMin + transMin);
        const a = piuGiorniLavorativi(partenza, lavMax + transMax);

        const stessoGiorno = da.getTime() === a.getTime();
        const testo = stessoGiorno
          ? fmtGiornoMese.format(da)
          : `${ripulisci(fmtGiorno.format(da))} e ${ripulisci(fmtGiornoMese.format(a))}`;

        uscita.textContent = stessoGiorno ? testo : testo;
        uscita.setAttribute('datetime', da.toISOString().slice(0, 10));

        const connettivo = this.querySelector('[data-yg-delivery-join]');
        if (connettivo) connettivo.textContent = stessoGiorno ? ' ' : ' tra ';

        // Il cut-off si mostra solo se ordinando adesso si rientra oggi.
        const boxCutoff = this.querySelector('[data-yg-delivery-cutoff]');
        if (boxCutoff) {
          const inTempo = ora.ora < cutoff && eLavorativo(aData(ora));
          if (inTempo) {
            const modello = this.dataset.cutoffText || 'Ordina entro le {ora}';
            boxCutoff.textContent = modello.replace('{ora}', `${cutoff}:00`);
            boxCutoff.hidden = false;
          } else {
            boxCutoff.hidden = true;
          }
        }

        this.classList.add('yg-delivery--ready');
      }
    }
  );
}
